import { FC, useState } from 'react';

import MoreVertIcon from '@mui/icons-material/MoreVert';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import {
  GridRenderCellParams,
  GridValidRowModel,
  useGridApiContext,
} from '@mui/x-data-grid-pro';

import Action from './ActionButton/types/action.type';

type ActionColumnProps = {
  actions?: Action[];
  currentCellParams: GridRenderCellParams<GridValidRowModel>;
};

const ActionColumn: FC<ActionColumnProps> = ({
  actions,
  currentCellParams,
}) => {
  const apiRef = useGridApiContext();

  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const open = Boolean(anchorEl);

  if (!actions) return null;

  return (
    <Stack alignItems='center' width='100%'>
      <IconButton size='medium' onClick={(e) => setAnchorEl(e.currentTarget)}>
        <MoreVertIcon sx={{ width: 14, height: 14 }} />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        id='table-settings-menu'
        open={open}
        onClose={() => setAnchorEl(null)}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              'minWidth': '150px',
              'overflow': 'visible',
              'filter': 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
              'mt': 1.5,
              '& .MuiAvatar-root': {
                width: 32,
                height: 32,
                ml: -0.5,
                mr: 1,
              },
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        {actions.map((action, idx) => {
          switch (action.type) {
            case 'default':
              return (
                <MenuItem
                  key={idx}
                  onClick={() =>
                    action.button.onClick?.(apiRef, currentCellParams.id)
                  }
                >
                  <Typography variant='body1' noWrap>
                    {action.button.label}
                  </Typography>

                  {action.button.divider && <Divider sx={{ my: 1 }} />}
                </MenuItem>
              );
            case 'custom':
              return action.button;
            default:
              return null;
          }
        })}
      </Menu>
    </Stack>
  );
};

export default ActionColumn;
